import React,{useEffect,useState} from 'react';
import Brand from '../components/Brand';
import {useQuoteViewTracking} from '../hooks/useQuoteViewTracking';
import PublicLineItems from '../components/PublicLineItems';
import PublicOptionalExtras from '../components/PublicOptionalExtras';
import PublicAcceptedExtras from '../components/PublicAcceptedExtras';
import QuoteSummary from '../components/QuoteSummary';
import AcceptanceSection from '../components/AcceptanceSection';
import {loadPublicQuote} from '../services/publicQuoteService';

function ClientQuote({shareToken}) {
  const [quote,setQuote]=useState(null);
  const [loadedToken,setLoadedToken]=useState(null);
  useQuoteViewTracking(shareToken, Boolean(quote) && loadedToken === shareToken);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState(false);
  const [selectedExtraIds,setSelectedExtraIds]=useState([]);

  useEffect(()=> {
    let active=true;

    setQuote(null);
    setLoadedToken(null);
    setLoading(true);
    setError(false);
    setSelectedExtraIds([]);

    loadPublicQuote(shareToken)
      .then((data)=> {
        if (!active) {
          return;
        }

        setQuote(data);
        setLoadedToken(shareToken);
        setLoading(false);
      })
      .catch(()=> {
        if (!active) {
          return;
        }

        setError(true);
        setLoading(false);
      });

    return ()=> {
      active=false;
    };
  },[shareToken]);

  const isAccepted=quote?.status==='accepted';
  const acceptance=quote?.acceptance;

  const toggleExtra=(extraId)=> {
    if (isAccepted) {
      return;
    }

    setSelectedExtraIds((current)=> current.includes(extraId)
      ? current.filter((id)=> id !==extraId)
      : [...current,extraId]
    );
  };

  const selectedExtrasTotalCents=(quote?.optional_extras || [])
    .filter((item)=> selectedExtraIds.includes(String(item.id)))
    .reduce((total,item)=> total + Number(item.price_cents || 0),0);

  const handleAccepted=(response)=> {
    if (response?.success || response?.already_accepted) {
      setQuote((current)=> current
        ? {
          ...current,
          status: 'accepted',
          acceptance: response.success
            ? {
              accepted_at: response.accepted_at,
              selected_extras: response.selected_extras,
              base_total_cents: response.base_total_cents,
              extras_total_cents: response.extras_total_cents,
              subtotal_ex_gst_cents: response.subtotal_ex_gst_cents,
              gst_cents: response.gst_cents,
              total_inc_gst_cents: response.total_inc_gst_cents
            }
            : current.acceptance
        }
        : current
      );
    }
  };

  if (loading) {
    return (
      <main className="client-page">
        <header className="proposal-header">
          <Brand />
        </header>
        <section className="proposal-intro">
          <span className="eyebrow">Quotation</span>
          <h1>Loading quotation…</h1>
        </section>
      </main>
    );
  }

  if (error || !quote) {
    return (
      <main className="client-page">
        <header className="proposal-header">
          <Brand />
        </header>
        <section className="proposal-intro">
          <span className="eyebrow">Quotation</span>
          <h1>Quotation not found</h1>
          <p>This quotation link may be invalid or no longer available.</p>
        </section>
      </main>
    );
  }

  const summaryProps=isAccepted
    ? {acceptedSnapshot: acceptance}
    : {
      baseTotalCents: quote.base_total_cents,
      selectedExtrasTotalCents
    };

  return (
    <main className="client-page">
      <header className="proposal-header">
        <Brand />
        <div className="proposal-meta">
          <span>Quote {quote.quote_reference}</span>
          <span>
            {quote.valid_until
              ? `Valid until ${new Intl.DateTimeFormat('en-AU').format(new Date(`${quote.valid_until}T00:00:00`))}`
              : 'Quotation validity available on request'}
          </span>
        </div>
      </header>

      <section className="proposal-intro">
        <span className="eyebrow">Prepared for {quote.client_company}</span>
        <h1>{quote.quote_title}</h1>
        <p>
          {quote.project_description || 'Please review the proposed production scope below.'}
        </p>
      </section>

      <div className="client-layout">
        <div className="proposal-content">
          <section className="proposal-section">
            <h2>Project Scope</h2>
            <PublicLineItems items={quote.line_items} />
          </section>

          {isAccepted ? (
            <PublicAcceptedExtras items={acceptance?.selected_extras} />
          ) : (
            <PublicOptionalExtras
              disabled={false}
              items={quote.optional_extras}
              onToggle={toggleExtra}
              selectedExtraIds={selectedExtraIds}
            />
          )}

          <div className="summary-mobile">
            <QuoteSummary {...summaryProps} />
          </div>

          <AcceptanceSection
            accepted={isAccepted}
            acceptedAt={acceptance?.accepted_at}
            clientCompany={quote.client_company}
            onAccepted={handleAccepted}
            selectedExtraIds={selectedExtraIds}
            shareToken={shareToken}
          />
        </div>

        <div className="summary-desktop">
          <QuoteSummary {...summaryProps} />
        </div>
      </div>

      <footer className="proposal-footer">
        <Brand />
        <p>Melbourne · Hobart &nbsp;|&nbsp;Premium video production</p>
        <p style={{fontSize:11}}>Quote opens are recorded, including IP address and approximate location, and may notify Angus Ashton Film.</p>
      </footer>
    </main>
  );
}

export default ClientQuote;
